/**
 * @fileoverview One-shot relay queries for the public read path.
 *
 * WHY nostr-tools AND NOT NDK. Space uses NDK because it needs a signer, the
 * outbox model, relay auth and long-lived subscriptions. The read path needs
 * none of those: it makes unauthenticated one-shot queries against relays named
 * in a URL and then closes them. NDK's default outbox pool would also send
 * lookups to purplepag.es and nos.lol on its own, which is a surprising amount
 * of third-party traffic for rendering one page.
 *
 * THE REVIEW CONDITION CAME DUE AND THE ANSWER DID NOT CHANGE. This used to say
 * "this app, in this task", with the exception due for review when the publish
 * path landed, because publishing needs a signer. It landed, and a signer turned
 * out not to be the thing NDK is for: the signer is `@cloistr/auth`'s, and
 * NIP-42 is the `onauth` callback forty lines below. What would reopen it is the
 * OUTBOX MODEL or a long-lived subscription -- publishing to an author's own
 * relay set, or a screen that stays subscribed. Recorded with that condition in
 * docs/integration-exceptions.md.
 *
 * FILTERS STAY NARROW. cloistr-relay caps filters at 50 kinds, 100 authors and
 * 500 ids (`rejectComplexFilters`; the kinds cap was a hardcoded 20 until
 * 2026-09-08 and refused ~9,800 filters in five hours). A refused filter comes
 * back as a bare CLOSED with no partial answer, so a fat filter looks exactly
 * like an empty relay. Every filter here names one kind and one author.
 */

import { boundedPoolPublish, settlePoolPublish } from '@cloistr/collab-common/relay';
import {
  SimplePool,
  type Event,
  type EventTemplate,
  type Filter,
  type VerifiedEvent,
} from 'nostr-tools';


import { config } from '@/config/environment';

/** Shared across queries so hint relays are not reconnected per page load. */
const pool = new SimplePool();

/**
 * Normalise a relay URL for deduplication.
 *
 * `wss://relay.cloistr.xyz` and `wss://relay.cloistr.xyz/` are the same relay
 * and open two sockets if both end up in the list.
 */
function normalise(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed.startsWith('ws://') && !trimmed.startsWith('wss://')) return null;
  return trimmed.replace(/\/+$/, '');
}

/**
 * Build the relay list for a query: the identifier's own hints FIRST, then our
 * fallbacks.
 *
 * The order is the point. An `naddr` names a page that may live on a relay we
 * have never heard of, and a renderer that asks our relay first -- or only --
 * cannot open anyone else's page. Our relay is the safety net for an `naddr`
 * that carries no hints, which is common because hints cost bech32 length.
 */
export function relaysFor(hints: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const url of [...hints, ...config.fallbackRelays]) {
    const n = normalise(url);
    if (n && !seen.has(n)) {
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

/**
 * Fetch every event matching a filter, across every relay given.
 *
 * Results are the union and may contain the same event more than once: two
 * relays holding the same page both answer, and for a REPLACEABLE event they
 * may hold two different versions of it. Deduplication needs to know what the
 * events mean -- by event id, or by addressable coordinate -- so it belongs to
 * the caller and not here.
 */
export async function fetchAll(
  relays: string[],
  filter: Filter,
  timeoutMs: number = config.relayTimeoutMs
): Promise<Event[]> {
  if (relays.length === 0) return [];

  try {
    return await pool.querySync(relays, filter, { maxWait: timeoutMs });
  } catch {
    // A relay that refuses, disconnects or never answers is not distinguishable
    // here from one that holds nothing, and must not be: see the indistinguish-
    // ability note in indexEvent.ts.
    return [];
  }
}

/**
 * Fetch every event matching a filter, then hand back the newest.
 *
 * Newest-wins rather than first-answer-wins because an addressable event is
 * REPLACEABLE: two relays can hold two versions of the same page, and the one
 * that answers first is whichever socket was quicker, not whichever is current.
 * Taking the first would make the rendered version depend on network timing.
 */
export async function fetchNewest(
  relays: string[],
  filter: Filter,
  timeoutMs: number = config.relayTimeoutMs
): Promise<Event | null> {
  const events = await fetchAll(relays, filter, timeoutMs);

  return events.reduce<Event | null>(
    (newest, e) => (!newest || e.created_at > newest.created_at ? e : newest),
    null
  );
}

/**
 * Ask every relay for a filter, and say WHICH RELAYS ACTUALLY ANSWERED.
 *
 * `fetchAll` cannot be used to decide that something does not exist, and must
 * not be: a relay that refuses, drops or never answers looks exactly like one
 * holding nothing. That is right for a reader and wrong for a publisher about
 * to write over a name. Measured in nostr-tools 2.25: a subscription's EOSE
 * timeout calls `oneose` just as a real EOSE does, so the library's own signal
 * cannot tell them apart either. Here the relay's own timeout is pushed past
 * ours, so `oneose` firing first means the relay really said "that is all".
 */
export async function probeRelays(
  relays: string[],
  filter: Filter,
  timeoutMs: number = config.relayTimeoutMs
): Promise<{ events: Event[]; unanswered: string[] }> {
  const perRelay = await Promise.all(
    relays.map(
      (url) =>
        new Promise<{ url: string; events: Event[]; answered: boolean }>((resolve) => {
          const events: Event[] = [];
          let done = false;
          let sub: { close: () => void } | null = null;
          const finish = (answered: boolean) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            try {
              sub?.close();
            } catch {
              // Closing a subscription on a dead socket can throw; it is closed.
            }
            resolve({ url, events, answered });
          };
          const timer = setTimeout(() => finish(false), timeoutMs);
          pool
            .ensureRelay(url, { connectionTimeout: timeoutMs })
            .then((relay) => {
              if (done) return;
              sub = relay.subscribe([filter], {
                onevent: (e) => events.push(e),
                oneose: () => finish(true),
                // A CLOSED from the relay, or the socket dropping: no answer.
                onclose: () => finish(false),
                eoseTimeout: timeoutMs * 10,
              });
            })
            .catch(() => finish(false));
        })
    )
  );
  return {
    events: perRelay.flatMap((r) => r.events),
    unanswered: perRelay.filter((r) => !r.answered).map((r) => r.url),
  };
}

/**
 * Send an event to every relay, reporting which ones took it.
 *
 * TWO THINGS THIS DOES THAT A BARE `pool.publish` DOES NOT.
 *
 * It ANSWERS THE RELAY'S AUTH CHALLENGE. Measured against relay.cloistr.xyz on
 * 2026-09-15: reads are open to anyone, but an unauthenticated EVENT is refused
 * with `OK false "auth-required: authentication required to publish events"`,
 * after the relay sends an `AUTH` challenge. So a publisher that does not
 * implement NIP-42 cannot write to our own relay at all. `signAuth` is handed
 * straight to the library, which produces the kind 22242 event and replays the
 * publish once it is accepted -- and it is the USER'S signer that signs it, so
 * the relay authenticates the author rather than us.
 *
 * It DOES NOT COLLAPSE THE FAILURES, which is the opposite of everything on the
 * read path. A reader must not be able to tell a private page from a missing
 * one, so `loadPage` flattens every outcome into one. An AUTHOR is the one
 * person entitled to know exactly what went wrong with their own page, and
 * "something went wrong" is precisely the message that leaves them retrying
 * forever. Each relay's refusal is kept, with its reason, verbatim.
 */
export async function publishEvent(
  relays: string[],
  event: Event,
  signAuth: (template: EventTemplate) => Promise<Event>,
  timeoutMs: number = config.publishTimeoutMs
): Promise<{ accepted: string[]; refused: { relay: string; reason: string }[] }> {
  // BOUNDED, BECAUSE THE LIBRARY'S PROMISE CAN NEVER SETTLE. Measured
  // 2026-10-06: when the relay's NIP-42 AUTH could not be signed, `publish`
  // neither resolved nor rejected. The CLI then exited 0 having printed nothing
  // and published nothing, and the editor would have spun forever. The bound
  // now lives in collab-common, which also bounds the signer on its own, so a
  // remote signer that never answers the sign-in is cut off too.
  //
  // Kept so a relay that went quiet after asking us to sign in is reported for
  // that reason, not as a bare timeout: the sign-in failure is the cause, and
  // the shared settle only knows that the relay timed out.
  let authFailure: string | null = null;
  // The shared signer bound rejects OUTSIDE this callback, so a signer that
  // never answers is seen here only as a sign-in that never finished.
  let authPending = false;
  const outcome = await settlePoolPublish(
    relays,
    boundedPoolPublish(pool, relays, event, {
      onauth: async (template: EventTemplate): Promise<VerifiedEvent> => {
        authPending = true;
        try {
          return (await signAuth(template)) as VerifiedEvent;
        } catch (e) {
          authFailure = e instanceof Error ? e.message : String(e);
          throw e;
        } finally {
          authPending = false;
        }
      },
      timeoutMs,
    })
  );
  const signInFailure = authFailure ?? (authPending ? 'the signer did not answer in time' : null);

  // The rejection carries the relay's own words when there are any. A socket
  // that closed carries nothing, and saying so plainly is better than
  // inventing a reason -- see the size pre-check, which exists so that the
  // commonest cause of a silent close never reaches this point.
  const refused = [
    ...outcome.rejected.map(({ relay, reason }) => ({
      relay,
      reason: reason || 'the relay closed the connection without saying why',
    })),
    ...outcome.timedOut.map((relay) => ({
      relay,
      reason: signInFailure
        ? `the relay asked to sign in (NIP-42) and that could not be signed: ${signInFailure}`
        : `the relay did not answer within ${Math.round(timeoutMs / 1000)}s`,
    })),
  ];
  // Back in the caller's relay order, which the shared settle splits apart.
  refused.sort((a, b) => relays.indexOf(a.relay) - relays.indexOf(b.relay));

  return { accepted: outcome.accepted, refused };
}

/** Close every socket. Called by tests and by a full page teardown. */
export function closeRelays(relays: string[]): void {
  pool.close(relays.map(normalise).filter((r): r is string => r !== null));
}
